import { ReactNode } from "react";

import {
    APP_MAX_WIDTH,
    ButtonSkeleton,
    Column,
    MediaStack,
    OutlinedCard,
    Row,
    SMALL_MAX_WIDTH,
    SkeletonRounded,
    StaticGrid,
    SxType,
    TextSkeleton,
    repeatElement
} from "@bridgesplit/ui";
import { Divider } from "@mui/material";

import { SideCardWrapper } from "./common";

// Intentionally using cheap primitives only
const TABLE_ROW_HEIGHT = 70;

function SkeletonPage({ maxWidth, spacing, children }: { maxWidth: string; spacing: number; children: ReactNode }) {
    return (
        <Column spacing={spacing} sx={{ maxWidth, width: "100%" }}>
            {children}
        </Column>
    );
}

function HeaderSkeleton({ statCount }: { statCount: number }) {
    return (
        <MediaStack spacing={2} spaceBetweenRow>
            <SkeletonRounded sx={{ width: "180px", height: "36px" }} />
            <Row spacing={4}>
                {repeatElement(
                    <Column spacing={0.75}>
                        <SkeletonRounded sx={{ width: "70px", height: "12px" }} />
                        <SkeletonRounded sx={{ width: "100px", height: "22px" }} />
                    </Column>,
                    statCount
                )}
            </Row>
        </MediaStack>
    );
}

function SubheaderSkeleton() {
    return <SkeletonRounded sx={{ width: "160px", height: "24px" }} />;
}

function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
    return (
        <OutlinedCard padding={2}>
            <Row spaceBetween sx={{ px: 1, pt: 0.5, pb: 1.5 }}>
                {repeatElement(<SkeletonRounded sx={{ width: "60px", height: "14px" }} />, columns)}
            </Row>
            <Column sx={{ px: 1, py: 2 }}>
                {repeatElement(
                    <SkeletonRounded sx={{ width: "100%", height: `${TABLE_ROW_HEIGHT}px`, mb: "2px" }} />,
                    rows
                )}
            </Column>
        </OutlinedCard>
    );
}

function FilterChipsSkeleton() {
    return <Row spacing={1}>{repeatElement(<SkeletonRounded sx={{ width: "90px", height: "32px" }} />, 4)}</Row>;
}

function CardBlock({ height }: { height: number }) {
    return <SkeletonRounded sx={{ width: "100%", height: `${height}px` }} />;
}

function PortfolioSectionSkeleton() {
    return (
        <Column spacing={2}>
            {/* h4 label; value + chevron are hidden while loading */}
            <SkeletonRounded sx={{ width: "150px", height: "28px" }} />
            <Divider />
            {/* matches SectionSkeleton's 200px block in PortfolioSections */}
            <SkeletonRounded sx={{ width: "100%", height: "200px" }} />
        </Column>
    );
}

export function LendSkeleton() {
    return (
        <SkeletonPage maxWidth={SMALL_MAX_WIDTH} spacing={6}>
            <HeaderSkeleton statCount={2} />
            <Column spacing={2}>
                <SubheaderSkeleton />
                <StaticGrid columns={3} mobileColumns={1}>
                    {repeatElement(<CardBlock height={286} />, 3)}
                </StaticGrid>
            </Column>
            <Column spacing={2}>
                <SubheaderSkeleton />
                <FilterChipsSkeleton />
                <TableSkeleton columns={6} />
            </Column>
            <Column spacing={2}>
                <SubheaderSkeleton />
                <TableSkeleton columns={5} />
            </Column>
        </SkeletonPage>
    );
}

export function EarnSkeleton() {
    return (
        <SkeletonPage maxWidth={SMALL_MAX_WIDTH} spacing={6}>
            <HeaderSkeleton statCount={2} />
            <Column spacing={2}>
                <SubheaderSkeleton />
                <CardBlock height={254} />
            </Column>
            <Column spacing={2}>
                <SubheaderSkeleton />
                <TableSkeleton columns={5} />
            </Column>
        </SkeletonPage>
    );
}

export function LoopsSkeleton() {
    return (
        <SkeletonPage maxWidth={SMALL_MAX_WIDTH} spacing={6}>
            <HeaderSkeleton statCount={2} />
            <Column spacing={2}>
                <SubheaderSkeleton />
                <CardBlock height={110} />
            </Column>
            <Column spacing={2}>
                <SubheaderSkeleton />
                <StaticGrid columns={3} mobileColumns={1}>
                    {repeatElement(<CardBlock height={274} />, 3)}
                </StaticGrid>
            </Column>
            <Column spacing={2}>
                <SubheaderSkeleton />
                <SkeletonRounded sx={{ width: "100%", height: "40px" }} />
                <FilterChipsSkeleton />
                <TableSkeleton columns={6} />
            </Column>
        </SkeletonPage>
    );
}

export function PortfolioSkeleton() {
    return (
        <SkeletonPage maxWidth={SMALL_MAX_WIDTH} spacing={2}>
            <HeaderSkeleton statCount={3} />
            {repeatElement(<PortfolioSectionSkeleton />, 5)}
        </SkeletonPage>
    );
}

export function DetailSkeleton() {
    return (
        <SkeletonPage maxWidth={APP_MAX_WIDTH} spacing={3}>
            <Column spacing={2}>
                <SkeletonRounded sx={{ width: "240px", height: "36px" }} />
                <TextSkeleton variant="caption" multiLine />
            </Column>
            <SideCardWrapper sideCard={<DetailSideCardSkeleton />}>
                <CardBlock height={320} />
                <CardBlock height={160} />
            </SideCardWrapper>
        </SkeletonPage>
    );
}

function DetailSideCardSkeleton() {
    const fieldSx: SxType = { width: "100%" };
    return (
        <OutlinedCard padding={2} spacing={2}>
            <SkeletonRounded sx={{ ...fieldSx, height: "40px" }} />
            <SkeletonRounded sx={{ ...fieldSx, height: "48px" }} />
            <SkeletonRounded sx={{ ...fieldSx, height: "90px" }} />
            <ButtonSkeleton />
        </OutlinedCard>
    );
}
